import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';

const SEVERITY_COLORS = {
  minor: '#22c55e',
  moderate: '#f59e0b',
  major: '#f97316',
  critical: '#dc2626',
};

const STATUS_LABELS = {
  reported: 'Reported',
  under_review: 'Under Review',
  work_order_created: 'WO Created',
  resolved: 'Resolved',
  closed: 'Closed',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr + 'Z').getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  return new Date(dateStr + 'Z').toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function IncidentQueuePage() {
  const navigate = useNavigate();
  const { user, isAtLeastAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  // Honor ?filter=accidents | incidents | active | all from the bucket
  // overview. Falls back to 'active' for direct nav.
  const initialFilter = (() => {
    const f = searchParams.get('filter');
    if (['accidents', 'incidents', 'active', 'all'].includes(f)) return f;
    return 'active';
  })();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState(initialFilter); // active, all, accidents, incidents
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showWOModal, setShowWOModal] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);

  // Work order creation form
  const [woForm, setWoForm] = useState({
    priority: 'normal',
    assigned_to: '',
    assigned_shop_id: '',
    wo_title: '',
    wo_description: '',
  });

  const loadData = useCallback(async () => {
    try {
      // Server-side filter for type-specific queries. Status filtering is
      // applied client-side so 'active' can sit on top of either type.
      const typeParam =
        filter === 'accidents' ? { incident_type: 'accident' } :
        filter === 'incidents' ? { incident_type: 'incident' } :
        {};
      const [incData, statsData] = await Promise.all([
        api.getIncidents(typeParam),
        api.getIncidentStats(),
      ]);
      let list = incData.incidents || [];
      if (filter === 'active' || filter === 'accidents' || filter === 'incidents') {
        list = list.filter(i => !['resolved', 'closed'].includes(i.status));
      }
      setIncidents(list);
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to load incidents');
    }
    setLoading(false);
  }, [filter]);

  const loadLookups = useCallback(async () => {
    try {
      const [t, s, u] = await Promise.all([
        api.getTrucks(),
        api.getShops(),
        api.getUsers ? api.getUsers() : Promise.resolve([]),
      ]);
      setTrucks(Array.isArray(t) ? t : (t.trucks || []));
      setShops(Array.isArray(s) ? s : (s.shops || []));
      setUsers(Array.isArray(u) ? u : (u.users || []));
    } catch (_) {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // Poll every 30s
  useEffect(() => {
    const timer = setInterval(loadData, 30000);
    return () => clearInterval(timer);
  }, [loadData]);

  async function handleReview(incident) {
    try {
      await api.reviewIncident(incident.id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleResolve(incident) {
    try {
      await api.resolveIncident(incident.id);
      loadData();
      setShowDetailModal(false);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleClose(incident) {
    try {
      await api.closeIncident(incident.id);
      loadData();
      setShowDetailModal(false);
    } catch (err) {
      setError(err.message);
    }
  }

  function openWOModal(incident) {
    setSelectedIncident(incident);
    setWoForm({
      priority: incident.severity === 'critical' ? 'urgent' : incident.severity === 'major' ? 'high' : 'normal',
      assigned_to: '',
      assigned_shop_id: '',
      wo_title: `${incident.incident_type === 'accident' ? 'Accident' : 'Incident'} Repair: ${incident.title}`,
      wo_description: incident.description,
    });
    setShowWOModal(true);
  }

  async function handleCreateWO(e) {
    e.preventDefault();
    try {
      await api.createIncidentWorkOrder(selectedIncident.id, {
        ...woForm,
        assigned_to: woForm.assigned_to ? parseInt(woForm.assigned_to) : null,
        assigned_shop_id: woForm.assigned_shop_id ? parseInt(woForm.assigned_shop_id) : null,
      });
      setShowWOModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  function openDetail(incident) {
    setSelectedIncident(incident);
    setShowDetailModal(true);
  }

  const isAdmin = isAtLeastAdmin || (user?.team_name || '') === 'Truck Maintenance' || user?.role === 'maintenance';

  return (
    <div className="incident-queue">
      <div className="incident-queue__header">
        <h1>Incidents & Accidents</h1>
        <div className="incident-queue__filters">
          {/* Back-to-bucket — depends on filter, defaults to Incidents */}
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => {
              const dest = filter === 'accidents' ? '/maintenance/accidents' : '/maintenance/incidents';
              navigate(dest);
            }}
          >
            ← Bucket
          </button>
          {['active', 'all', 'incidents', 'accidents'].map(f => (
            <button
              key={f}
              className={`btn btn-sm ${filter === f ? 'btn-gold' : 'btn-outline'}`}
              onClick={() => setFilter(f)}
            >
              {f === 'active' ? 'Active' :
               f === 'all' ? 'All' :
               f === 'incidents' ? 'Incidents Only' :
               'Accidents Only'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="kpi-grid">
          <div className="kpi-card" style={{ '--kpi-accent': '#dc2626' }}>
            <div className="kpi-value">{stats.reported}</div>
            <div className="kpi-label">Reported</div>
          </div>
          <div className="kpi-card" style={{ '--kpi-accent': '#f59e0b' }}>
            <div className="kpi-value">{stats.under_review}</div>
            <div className="kpi-label">Under Review</div>
          </div>
          <div className="kpi-card" style={{ '--kpi-accent': '#3b82f6' }}>
            <div className="kpi-value">{stats.work_order_created}</div>
            <div className="kpi-label">WO Created</div>
          </div>
          <div className="kpi-card" style={{ '--kpi-accent': '#22c55e' }}>
            <div className="kpi-value">{stats.resolved}</div>
            <div className="kpi-label">Resolved</div>
          </div>
          {stats.open_accidents > 0 && (
            <div className="kpi-card" style={{ '--kpi-accent': '#dc2626' }}>
              <div className="kpi-value">{stats.open_accidents}</div>
              <div className="kpi-label">Open Accidents</div>
            </div>
          )}
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading incidents...</div>
      ) : incidents.length === 0 ? (
        <div className="empty-state">
          <p>No incidents found.</p>
        </div>
      ) : (
        <div className="incident-cards">
          {incidents.map(inc => (
            <div
              key={inc.id}
              className={`incident-card ${inc.severity === 'critical' ? 'incident-card--critical' : ''}`}
              onClick={() => openDetail(inc)}
            >
              <div className="incident-card__top">
                <div className="incident-card__badges">
                  <span
                    className="incident-card__type"
                    style={{ background: inc.incident_type === 'accident' ? '#dc262620' : '#f59e0b20', color: inc.incident_type === 'accident' ? '#dc2626' : '#f59e0b' }}
                  >
                    {inc.incident_type === 'accident' ? '🚨 Accident' : '⚠️ Incident'}
                  </span>
                  <span className="incident-card__severity" style={{ background: `${SEVERITY_COLORS[inc.severity]}20`, color: SEVERITY_COLORS[inc.severity] }}>
                    {inc.severity.toUpperCase()}
                  </span>
                  <span className="incident-card__status">{STATUS_LABELS[inc.status]}</span>
                </div>
                <span className="incident-card__time">{timeAgo(inc.created_at)}</span>
              </div>

              <h3 className="incident-card__title">{inc.title}</h3>
              <p className="incident-card__desc">{inc.description.length > 120 ? inc.description.slice(0, 120) + '...' : inc.description}</p>

              <div className="incident-card__meta">
                <span>Truck: <strong>{inc.truck_unit || 'N/A'}</strong></span>
                <span>By: {inc.reported_by_name}</span>
                {inc.photo_count > 0 && <span>📷 {inc.photo_count}</span>}
                {inc.injuries > 0 && <span style={{ color: '#dc2626' }}>🩹 {inc.injuries} injury{inc.injuries !== 1 ? 'ies' : ''}</span>}
              </div>

              {isAdmin && inc.status === 'reported' && (
                <div className="incident-card__actions" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-sm btn-outline" onClick={() => handleReview(inc)}>Start Review</button>
                  <button className="btn btn-sm btn-gold" onClick={() => openWOModal(inc)}>Create Work Order</button>
                </div>
              )}
              {isAdmin && inc.status === 'under_review' && (
                <div className="incident-card__actions" onClick={e => e.stopPropagation()}>
                  <button className="btn btn-sm btn-gold" onClick={() => openWOModal(inc)}>Create Work Order</button>
                  <button className="btn btn-sm btn-outline" onClick={() => handleResolve(inc)}>Resolve</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedIncident && (
        <Modal title={`Incident #${selectedIncident.id}`} onClose={() => setShowDetailModal(false)} wide>
          <div className="incident-detail">
            <div className="incident-detail__badges">
              <span style={{ background: selectedIncident.incident_type === 'accident' ? '#dc262620' : '#f59e0b20', color: selectedIncident.incident_type === 'accident' ? '#dc2626' : '#f59e0b', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                {selectedIncident.incident_type === 'accident' ? '🚨 Accident' : '⚠️ Incident'}
              </span>
              <span style={{ background: `${SEVERITY_COLORS[selectedIncident.severity]}20`, color: SEVERITY_COLORS[selectedIncident.severity], padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem' }}>
                {selectedIncident.severity.toUpperCase()}
              </span>
              <span style={{ background: '#6b728020', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem' }}>
                {STATUS_LABELS[selectedIncident.status]}
              </span>
            </div>

            <h2 style={{ margin: '16px 0 8px' }}>{selectedIncident.title}</h2>
            <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{selectedIncident.description}</p>

            <div className="incident-detail__grid">
              <div><strong>Truck:</strong> {selectedIncident.truck_unit} {selectedIncident.truck_make} {selectedIncident.truck_model}</div>
              <div><strong>Occurred:</strong> {formatDate(selectedIncident.occurred_at)}</div>
              <div><strong>Reported by:</strong> {selectedIncident.reported_by_name}</div>
              <div><strong>Reported:</strong> {formatDate(selectedIncident.created_at)}</div>
              {selectedIncident.location && <div><strong>Location:</strong> {selectedIncident.location}</div>}
              <div><strong>Drivable:</strong> {selectedIncident.drivable ? 'Yes' : 'No'}</div>
              <div><strong>Tow needed:</strong> {selectedIncident.tow_needed ? 'Yes' : 'No'}</div>
              <div><strong>Injuries:</strong> {selectedIncident.injuries || 0}</div>
              {selectedIncident.injury_details && <div style={{ gridColumn: '1 / -1' }}><strong>Injury Details:</strong> {selectedIncident.injury_details}</div>}
              <div><strong>Police called:</strong> {selectedIncident.police_called ? 'Yes' : 'No'}</div>
              {selectedIncident.police_report_number && <div><strong>Report #:</strong> {selectedIncident.police_report_number}</div>}
              <div><strong>Third party:</strong> {selectedIncident.third_party_involved ? 'Yes' : 'No'}</div>
              {selectedIncident.third_party_details && <div style={{ gridColumn: '1 / -1' }}><strong>Third Party Details:</strong> {selectedIncident.third_party_details}</div>}
              {selectedIncident.reviewed_by_name && <div><strong>Reviewed by:</strong> {selectedIncident.reviewed_by_name}</div>}
              {selectedIncident.review_notes && <div style={{ gridColumn: '1 / -1' }}><strong>Review Notes:</strong> {selectedIncident.review_notes}</div>}
              {selectedIncident.work_order_id && <div><strong>Work Order:</strong> #{selectedIncident.work_order_id}</div>}
            </div>

            {isAdmin && (
              <div className="modal-actions" style={{ marginTop: '20px' }}>
                {selectedIncident.status === 'reported' && (
                  <>
                    <button className="btn btn-outline" onClick={() => handleReview(selectedIncident)}>Start Review</button>
                    <button className="btn btn-gold" onClick={() => { setShowDetailModal(false); openWOModal(selectedIncident); }}>Create Work Order</button>
                  </>
                )}
                {selectedIncident.status === 'under_review' && (
                  <>
                    <button className="btn btn-gold" onClick={() => { setShowDetailModal(false); openWOModal(selectedIncident); }}>Create Work Order</button>
                    <button className="btn btn-outline" onClick={() => handleResolve(selectedIncident)}>Resolve</button>
                  </>
                )}
                {selectedIncident.status === 'work_order_created' && (
                  <button className="btn btn-outline" onClick={() => handleResolve(selectedIncident)}>Resolve</button>
                )}
                {selectedIncident.status === 'resolved' && isAdmin && (
                  <button className="btn btn-outline" onClick={() => handleClose(selectedIncident)}>Close</button>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Create Work Order Modal */}
      {showWOModal && selectedIncident && (
        <Modal title="Create Work Order from Incident" onClose={() => setShowWOModal(false)}>
          <form onSubmit={handleCreateWO}>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Work Order Title</label>
                <input
                  name="wo_title"
                  value={woForm.wo_title}
                  onChange={e => setWoForm(f => ({ ...f, wo_title: e.target.value }))}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select
                  value={woForm.priority}
                  onChange={e => setWoForm(f => ({ ...f, priority: e.target.value }))}
                  className="form-input"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={woForm.assigned_to}
                  onChange={e => setWoForm(f => ({ ...f, assigned_to: e.target.value }))}
                  className="form-input"
                >
                  <option value="">Unassigned</option>
                  {users.filter(u => u.is_active).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Assign to Shop</label>
                <select
                  value={woForm.assigned_shop_id}
                  onChange={e => setWoForm(f => ({ ...f, assigned_shop_id: e.target.value }))}
                  className="form-input"
                >
                  <option value="">No shop</option>
                  {shops.filter(s => s.is_active !== 0).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea
                  value={woForm.wo_description}
                  onChange={e => setWoForm(f => ({ ...f, wo_description: e.target.value }))}
                  className="form-input"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowWOModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold">Create Work Order</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
